import * as React from "react";
import { Command as CommandPrimitive } from "cmdk";
import { X } from "lucide-react";
import { Badge } from "./badge";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "./command";
import { cn } from "@/lib/utils";
import { logger } from "@/utils/logger";

const MultiSelect = React.forwardRef(
  ({ className, value = [], onChange, placeholder, options = [], ...props }, ref) => {
    const inputRef = React.useRef(null);
    const [open, setOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState("");

    const handleUnselect = (item) => {
      onChange(value.filter((i) => i !== item));
    };

    const handleSelect = (selectedOption) => {
      logger.log('MultiSelect: Selecting option:', selectedOption);
      if (!value.includes(selectedOption)) {
        onChange([...value, selectedOption]);
      }
      setInputValue("");
      setOpen(false);
    };

    const handleKeyDown = (e) => {
      const input = inputRef.current;
      if (input) {
        if (e.key === "Delete" || e.key === "Backspace") {
          if (input.value === "" && value.length > 0) {
            handleUnselect(value[value.length - 1]);
          }
        }
        if (e.key === "Escape") {
          input.blur();
          setOpen(false);
        }
        if (e.key === "Enter" && inputValue.trim()) {
          // Try to find an exact match
          const exactMatch = options.find(option => 
            option.toLowerCase() === inputValue.toLowerCase()
          );
          if (exactMatch) {
            handleSelect(exactMatch);
          }
        }
      }
    };

    // Filter options based on input value
    const selectables = options.filter((option) => 
      !value.includes(option) && 
      option.toLowerCase().includes(inputValue.toLowerCase())
    );

    return (
      <Command
        onKeyDown={handleKeyDown}
        className={cn(
          "overflow-visible bg-transparent",
          className
        )}
        {...props}
      >
        <div
          className="group rounded-md border border-input px-3 py-2 text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
        >
          <div className="flex flex-wrap gap-1">
            {value.map((item) => (
              <Badge
                key={item}
                variant="secondary"
                className="rounded-sm px-1 font-normal"
              >
                {item}
                <button
                  className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleUnselect(item);
                    }
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={() => handleUnselect(item)}
                >
                  <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                </button>
              </Badge>
            ))}
            <CommandInput
              ref={inputRef}
              value={inputValue}
              onValueChange={(newValue) => {
                setInputValue(newValue);
                setOpen(true);
              }}
              onBlur={() => setTimeout(() => setOpen(false), 200)}
              onFocus={() => setOpen(true)}
              placeholder={value.length === 0 ? placeholder : "Type to search..."}
              className="ml-2 bg-transparent outline-none placeholder:text-muted-foreground flex-1"
            />
          </div>
        </div>
        <div className="relative">
          {open && selectables.length > 0 ? (
            <div className="absolute top-full z-50 w-full rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in">
              <CommandGroup className="max-h-[200px] overflow-auto">
                {selectables.map((option) => (
                  <CommandItem
                    key={option}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={() => handleSelect(option)}
                    onSelect={() => handleSelect(option)}
                    className={"cursor-pointer"}
                  >
                    {option}
                  </CommandItem>
                ))}
              </CommandGroup>
            </div>
          ) : null}
        </div>
      </Command>
    );
  }
);

MultiSelect.displayName = "MultiSelect";

export { MultiSelect };
